/**
 * Calling from a conversation.
 *
 * A call in a direct message or a group is the same voice mesh a party uses,
 * pointed at a different set of people. The server widened the idea of a
 * "room" from a party id to any of three - a party, a group, or a pair - so
 * nothing here has to reimplement peers, mute, or screen sharing. All this
 * does is name the room and offer a way in and out.
 */

import { useMemo, useState } from 'react';
import { useVoice } from '@/stores/voice';
import { useSession } from '@/stores/session';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { ScreenStage } from './ScreenStage';

/**
 * The room id for a conversation.
 *
 * A pair is sorted so both ends name the room identically - without that, two
 * people would ring each other into two different rooms and neither would
 * hear a thing. A group is simply its own id.
 */
export function dmRoom(meId: string, otherId: string): string {
  return 'dm:' + [meId, otherId].sort().join('|');
}

export function groupRoom(groupId: string): string {
  return groupId;
}

/** Join or hang up, as one button in a conversation header. */
export function CallButton({ room, label }: { room: string | null; label: string }) {
  const voice = useVoice();
  const here = !!room && voice.partyId === room;

  if (!room) return null;

  return (
    <button
      type="button"
      onClick={() => { if (here) void voice.leave(); else void voice.join(room); }}
      title={here ? 'Leave the call' : label}
      aria-label={here ? 'Leave the call' : label}
      className={cn(
        'grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] transition-colors',
        here
          ? 'bg-[color-mix(in_srgb,var(--bad)_20%,transparent)] text-bad hover:bg-[color-mix(in_srgb,var(--bad)_28%,transparent)]'
          : 'text-mute hover:bg-surface-2 hover:text-voice'
      )}
    >
      <Icon name={here ? 'callEnd' : 'call'} size={17} />
    </button>
  );
}

/**
 * Who is on the call, above the composer.
 *
 * Only drawn while there is a call to be in. An empty strip saying nobody is
 * talking is furniture, and this screen is for reading messages.
 *
 * Everyone on the call carries their own state - talking, muted, deafened -
 * because a silent call is ambiguous otherwise: you cannot tell somebody who
 * has nothing to say from somebody whose microphone is off. The controls are
 * the same three the dock has, put where you are already looking, and the
 * whole strip folds away for anyone who would rather have the room back.
 */
export function CallBar({ room, people }: { room: string | null; people: PublicUser[] }) {
  const voice = useVoice();
  const me = useSession((s) => s.me);
  const settings = useSession((st) => st.settings);
  const here = !!room && voice.partyId === room;
  const [folded, setFolded] = useState(false);

  /* Everyone in the room, resolved to the people this screen already knows,
     keeping the voice state rather than flattening to a user. */
  const inCall = useMemo(() => {
    if (!here) return [];
    const byId = new Map(people.map((p) => [p.id, p]));
    if (me) byId.set(me.id, me as PublicUser);
    return voice.members
      .map((m) => ({ ...m, who: m.user ?? byId.get(m.userId) ?? null }))
      .filter((m) => m.who);
  }, [here, voice.members, people, me]);

  if (!here) return null;

  const alone = inCall.length < 2;

  return (
    <>
      {/*
        * Whatever anyone on this call is sharing.
        *
        * The mesh has always carried the video - the track is added to every
        * peer and renegotiated, and the receiving side files it under that
        * person. It was simply never drawn outside a party: ScreenStage was
        * mounted in PartyView alone, so sharing your screen in a direct
        * message or a group sent a perfectly good stream to people who had
        * nowhere at all to see it.
        *
        * Mounted here because this component is the one thing that already
        * knows you are in *this* conversation's call, so a party share can
        * never leak into a direct message.
        */}
      <ScreenStage />

    <div className="mx-[var(--pad)] mb-2 rounded-[var(--radius)] border border-[color-mix(in_srgb,var(--voice)_30%,var(--line))] bg-[color-mix(in_srgb,var(--voice)_7%,var(--bg-2))]">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <span className="grid h-8 w-8 flex-none place-items-center text-voice">
          <Icon name="call" size={17} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-bold">
            {alone ? 'Waiting for an answer' : 'In a call'}
          </span>
          <span className="block truncate text-[11.5px] text-mute">
            {alone
              ? 'Ringing. Nobody else has joined yet.'
              : inCall.map((m) => m.who!.displayName).join(', ')}
          </span>
        </span>

        {/* Your own three, in the order the dock has them. */}
        <CallToggle
          on={voice.muted}
          onIcon="micOff"
          offIcon="mic"
          label={voice.muted ? 'Unmute' : 'Mute'}
          onClick={() => voice.toggleMute()}
        />
        <CallToggle
          on={voice.deafened}
          onIcon="headsetOff"
          offIcon="headset"
          label={voice.deafened ? 'Undeafen' : 'Deafen'}
          onClick={() => voice.toggleDeafen()}
        />

        <CallToggle
          on={voice.sharing}
          onIcon="close"
          offIcon="layers"
          label={voice.sharing ? 'Stop sharing' : 'Share your screen'}
          tone={voice.sharing ? 'live' : 'off'}
          onClick={() => {
            if (voice.sharing) voice.stopScreenShare();
            else void voice.startScreenShare(settings?.screenQuality ?? 'balanced');
          }}
        />

        <button
          type="button"
          onClick={() => void voice.leave()}
          title="Hang up"
          aria-label="Hang up"
          className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--bad)_20%,transparent)] text-bad transition-colors hover:bg-[color-mix(in_srgb,var(--bad)_32%,transparent)]"
        >
          <Icon name="callEnd" size={16} />
        </button>

        <button
          type="button"
          onClick={() => setFolded((f) => !f)}
          title={folded ? 'Show everyone' : 'Hide the faces'}
          aria-label={folded ? 'Show everyone' : 'Hide the faces'}
          aria-expanded={!folded}
          className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-2 hover:text-ink"
        >
          <Icon name={folded ? 'chevronD' : 'chevronR'} size={16} />
        </button>
      </div>

      {folded ? null : (
        <div className="flex flex-wrap gap-1.5 px-3 pb-3">
          {inCall.map((m) => (
            <CallFace
              key={m.userId}
              user={m.who!}
              speaking={m.speaking && !m.muted}
              muted={m.muted}
              deafened={m.deafened}
              you={m.userId === me?.id}
            />
          ))}
        </div>
      )}
    </div>
    </>
  );
}

/**
 * One of your own controls: lit when the thing it names is on.
 *
 * Two tones, because "on" does not always mean the same thing. Muted and
 * deafened are states you have taken something away with, and they read in the
 * warning colour; sharing is a thing you are actively doing, and painting that
 * red would say something had gone wrong.
 */
function CallToggle({ on, onIcon, offIcon, label, onClick, tone = 'off' }: {
  on: boolean;
  onIcon: string;
  offIcon: string;
  label: string;
  onClick: () => void;
  tone?: 'off' | 'live';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={on}
      className={cn(
        'grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] transition-colors',
        on && tone === 'live'
          ? 'bg-[color-mix(in_srgb,var(--voice)_20%,transparent)] text-voice hover:bg-[color-mix(in_srgb,var(--voice)_30%,transparent)]'
          : on
            ? 'bg-[color-mix(in_srgb,var(--bad)_18%,transparent)] text-bad hover:bg-[color-mix(in_srgb,var(--bad)_28%,transparent)]'
            : 'text-mute hover:bg-surface-2 hover:text-ink'
      )}
    >
      <Icon name={(on ? onIcon : offIcon) as never} size={16} />
    </button>
  );
}

/**
 * One person on the call.
 *
 * Deafened outranks muted in the badge: somebody who cannot hear you is a
 * more useful thing to know than somebody who is not speaking, and a deafened
 * person is always muted anyway.
 */
function CallFace({ user, speaking, muted, deafened, you }: {
  user: PublicUser;
  speaking: boolean;
  muted: boolean;
  deafened: boolean;
  you: boolean;
}) {
  const badge = deafened ? 'headsetOff' : muted ? 'micOff' : null;

  return (
    <span
      title={user.displayName + (deafened ? ' - deafened' : muted ? ' - muted' : '')}
      className={cn(
        'flex items-center gap-2 rounded-full border py-1 pl-1 pr-2.5 transition-colors',
        speaking
          ? 'border-[color-mix(in_srgb,var(--voice)_60%,transparent)] bg-[color-mix(in_srgb,var(--voice)_14%,transparent)]'
          : 'border-line-soft bg-surface-2'
      )}
    >
      <span className="relative flex-none">
        <Avatar user={user} size={22} />
        {badge ? (
          <span className="absolute -bottom-0.5 -right-0.5 grid h-3.5 w-3.5 place-items-center rounded-full bg-bg-2 text-bad">
            <Icon name={badge as never} size={9} />
          </span>
        ) : null}
      </span>
      <span className={cn('text-[11.5px]', muted || deafened ? 'text-mute' : 'font-semibold')}>
        {you ? 'You' : <UserName user={user} profile={false} className="inline-block max-w-[120px] align-bottom" />}
      </span>
    </span>
  );
}
